import { ref, reactive, nextTick } from 'vue'
import StreamService from '@/services/StreamService'

export function useDestinationGroups({
	stream,
	streamPlatforms,
	deps,
}) {
	const groups = ref([])
	const groupToggleStateMap = reactive({})
	const collapsedGroups = reactive({})
	const groupToggleProcessing = ref(false)
	const groupName = ref('')
	const selectedOptions = ref([])
	const currentGroupName = ref('')

	const showEditGroupModal = ref(false)
	const showDeleteGroupModal = ref(false)
	const showMoveToGroupModal = ref(false)
	const groupToEdit = ref('')
	const editedGroupName = ref('')
	const groupToDelete = ref(null)
	const platformToMove = ref(null)
	const targetGroupName = ref('')

	function getPlatformById(id) {
		return streamPlatforms.value.find((p) => p._id === id)
	}

	function getSelectedGroup(ids) {
		return ids.map((id) => streamPlatforms.value.find((p) => p._id === id)).filter(Boolean)
	}

	async function saveDestinationGroup() {
		await StreamService.saveStreamMetadata(stream.value._id, 'destinationGroups', groups.value)
	}

	function computeGroupToggleStateForGroup(groupNameValue) {
		const group = groups.value.find((g) => g.name === groupNameValue)
		if (!group) return
		const platforms = group.selected.map((id) => getPlatformById(id)).filter(Boolean)
		groupToggleStateMap[group.name] = platforms.every((p) => p.enabled === true)
	}

	async function toggleGroupedPlatforms(name) {
		const newState = groupToggleStateMap[name]
		const group = groups.value.find((g) => g.name === name)
		if (!group) return
		groupToggleProcessing.value = true
		try {
			await deps.setPlatformsEnabled(getSelectedGroup(group.selected), newState)
		} finally {
			computeGroupToggleStateForGroup(name)
			deps.computeGroupToggleState()
			groupToggleProcessing.value = false
		}
	}

	function toggleGroup(name) {
		collapsedGroups[name] = !collapsedGroups[name]
	}

	function isGroupCollapsed(name) {
		return collapsedGroups[name]
	}

	function requestGroupEdit(name) {
		groupToEdit.value = name
		editedGroupName.value = name
		showEditGroupModal.value = true
	}

	function confirmGroupEdit() {
		const group = groups.value.find((g) => g.name === groupToEdit.value)
		const nextName = editedGroupName.value.trim()
		if (group && nextName && group.name !== nextName) {
			groupToggleStateMap[nextName] = groupToggleStateMap[groupToEdit.value]
			delete groupToggleStateMap[groupToEdit.value]
			group.name = nextName
		}
		groupToEdit.value = ''
		editedGroupName.value = ''
		showEditGroupModal.value = false
		saveDestinationGroup()
	}

	function deleteGroup(name) {
		groupToDelete.value = name
		showDeleteGroupModal.value = true
	}

	function cancelDeleteGroup() {
		groupToDelete.value = null
		showDeleteGroupModal.value = false
	}

	function confirmDeleteGroup() {
		const name = groupToDelete.value
		const groupIndex = groups.value.findIndex((g) => g.name === name)
		if (groupIndex !== -1) {
			groups.value.splice(groupIndex, 1)
			delete groupToggleStateMap[name]
		}
		groupToDelete.value = null
		showDeleteGroupModal.value = false
		saveDestinationGroup()
	}

	function requestPlatformMoveToGroup(platform) {
		platformToMove.value = platform
		targetGroupName.value = ''
		showMoveToGroupModal.value = true
	}

	function confirmMoveToGroup() {
		if (!targetGroupName.value || !platformToMove.value) return
		const targetGroup = groups.value.find((g) => g.name === targetGroupName.value)
		const platformId = platformToMove.value._id
		groups.value.forEach((group) => {
			const index = group.selected.indexOf(platformId)
			if (index !== -1) group.selected.splice(index, 1)
		})
		if (targetGroup) {
			targetGroup.selected.push(platformId)
		} else {
			groups.value.push({ name: targetGroupName.value, selected: [platformId] })
		}
		computeGroupToggleStateForGroup(targetGroupName.value)
		platformToMove.value = null
		targetGroupName.value = ''
		showMoveToGroupModal.value = false
		saveDestinationGroup()
	}

	function onCreateClicked(data) {
		if (!data.name) return
		groups.value.push({
			name: data.name,
			selected: [...data.selected],
		})
		nextTick(() => {
			computeGroupToggleStateForGroup(data.name)
		})
		selectedOptions.value = []
		groupName.value = ''
		saveDestinationGroup()
	}

	function handleCancel() {
		selectedOptions.value = []
		groupName.value = ''
	}

	function setCurrentGroupName(name) {
		currentGroupName.value = name
	}

	function openAddPlatformForGroup(name) {
		setCurrentGroupName(name)
	}

	function addPlatformToGroup(platform, groupNameValue) {
		const group = groups.value.find((g) => g.name === groupNameValue)
		if (group) {
			group.selected.push(platform._id)
			currentGroupName.value = ''
			// Persist the new membership — without this the destination shows in the
			// group in-session but reverts to ungrouped on reload (groups are only
			// re-read from stream.metadata.destinationGroups on mount). Mirrors the
			// save every other group mutation already does.
			saveDestinationGroup()
		}
	}

	return {
		groups,
		groupToggleStateMap,
		collapsedGroups,
		groupToggleProcessing,
		groupName,
		selectedOptions,
		currentGroupName,
		showEditGroupModal,
		showDeleteGroupModal,
		showMoveToGroupModal,
		groupToEdit,
		editedGroupName,
		groupToDelete,
		platformToMove,
		targetGroupName,
		getPlatformById,
		getSelectedGroup,
		saveDestinationGroup,
		computeGroupToggleStateForGroup,
		toggleGroupedPlatforms,
		toggleGroup,
		isGroupCollapsed,
		requestGroupEdit,
		confirmGroupEdit,
		deleteGroup,
		cancelDeleteGroup,
		confirmDeleteGroup,
		requestPlatformMoveToGroup,
		confirmMoveToGroup,
		onCreateClicked,
		handleCancel,
		setCurrentGroupName,
		openAddPlatformForGroup,
		addPlatformToGroup,
	}
}
